/**
 * Client-side evaluator for a plugin_action `record_filter`.
 *
 * A `record_filter` decides whether a plugin action button is shown for the
 * record currently on screen. The match is evaluated **client-side** against the
 * already-loaded record (button visibility must be synchronous — no DB query),
 * so this module reimplements the subset of the advanced-search filter grammar
 * that can be answered from an in-memory record.
 *
 * Two filter shapes are supported:
 *
 *  1. NEW grammar — the advanced-search filter DSL (see
 *     `containers/AdvancedSearch`, `SingleField` in `utils/utils.ts`, and the
 *     server-side compiler `sarest/table/select_mixin.py`):
 *       { and: [ leaf | group, ... ] }   — all children must match
 *       { or:  [ leaf | group, ... ] }   — any child must match
 *     A bare top-level leaf `{ field, op, value }` (an unwrapped single comparison) is also
 *     valid grammar and is treated as a one-condition filter.
 *     where a leaf is `{ field, op, value }`. `field` may be a dot-path into the
 *     record (e.g. `value.status` reads `record.value.status`, so custom-object
 *     attributes work). Supported operators mirror the backend:
 *       eq, neq, like, notlike, lt, lte, gt, gte, set, unset, in, notin.
 *
 *  2. LEGACY flat map — the historical plugin_action shape `{ key: value, ... }`
 *     matched OR-across-keys with strict equality against top-level record
 *     columns. Preserved verbatim for backward compatibility with existing
 *     filters until they are migrated to the new grammar.
 *
 * Value-position placeholders (resolved per viewer, mirroring the server's
 * `_resolve_filter_placeholder`): `@me` → current identity id, `@workspace` →
 * current workspace id, `@now` → current ISO timestamp, `@today` → start of the
 * current UTC day (ISO). When a required placeholder cannot be resolved (e.g.
 * `@me` with no logged-in user), the comparison is treated as non-matching (the
 * button hides) rather than throwing.
 *
 * `exists` / `not.exists` (has-related-record) nodes are intentionally NOT
 * supported here — they require a DB round-trip, which is wrong for synchronous
 * button visibility. Such a node evaluates to `false` (non-matching).
 */

export interface RecordFilterContext {
    /** Current identity id — substituted for the `@me` placeholder. */
    currentUserId?: string | null;
    /** Current workspace id — substituted for the `@workspace` placeholder. */
    workspaceId?: string | null;
    /** Epoch ms used to resolve `@now` / `@today`; defaults to `Date.now()`. */
    now?: number;
}

type RecordLike = { [key: string]: unknown };

interface Leaf {
    field?: string;
    op?: string;
    value?: unknown;
}

/** Sentinel: a placeholder that could not be resolved for the current viewer. */
const UNRESOLVED: unique symbol = Symbol("unresolved-placeholder");

/** Read a (possibly dot-pathed) field out of a record, returning undefined when absent. */
const getFieldValue = (record: RecordLike | null | undefined, path?: string): unknown => {
    if (record == null || !path) {
        return undefined;
    }
    return path.split(".").reduce<unknown>((acc, key) => {
        if (acc == null || typeof acc !== "object") {
            return undefined;
        }
        return (acc as RecordLike)[key];
    }, record);
};

/** Resolve a magic `@`-placeholder value; non-placeholders pass through unchanged. */
const resolvePlaceholder = (
    value: unknown,
    ctx: RecordFilterContext
): unknown | typeof UNRESOLVED => {
    if (typeof value !== "string") {
        return value;
    }
    switch (value) {
        case "@me":
            return ctx.currentUserId ? String(ctx.currentUserId) : UNRESOLVED;
        case "@workspace":
            return ctx.workspaceId ? String(ctx.workspaceId) : UNRESOLVED;
        case "@now":
            return new Date(ctx.now ?? Date.now()).toISOString();
        case "@today": {
            const d = new Date(ctx.now ?? Date.now());
            d.setUTCHours(0, 0, 0, 0);
            return d.toISOString();
        }
        default:
            return value;
    }
};

/** Translate a SQL LIKE pattern (`%` any-run, `_` any-char) to a case-insensitive RegExp. */
const likeToRegExp = (pattern: unknown): RegExp => {
    const escaped = String(pattern).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const translated = escaped.replace(/%/g, ".*").replace(/_/g, ".");
    return new RegExp(`^${translated}$`, "i");
};

/**
 * Equality that tolerates the string/number/boolean type drift between a
 * JSON-loaded record value and a DSL filter value (which often arrives as a
 * string from a text control). Strict identity first, then boolean- and
 * numeric-tolerant comparison.
 */
const looseEq = (a: unknown, b: unknown): boolean => {
    if (a === b) {
        return true;
    }
    if (a == null || b == null) {
        return false;
    }
    if (typeof a === "boolean" || typeof b === "boolean") {
        return String(a) === String(b);
    }
    if (a !== "" && b !== "" && !Number.isNaN(Number(a)) && !Number.isNaN(Number(b))) {
        return Number(a) === Number(b);
    }
    return false;
};

/** Evaluate one leaf comparison `{ field, op, value }` against a resolved field value. */
const evalLeaf = (fieldValue: unknown, leaf: Leaf, ctx: RecordFilterContext): boolean => {
    const op = leaf.op ?? "eq";

    if (op === "set") {
        return fieldValue !== null && fieldValue !== undefined;
    }
    if (op === "unset") {
        return fieldValue === null || fieldValue === undefined;
    }

    if (op === "in" || op === "notin") {
        const rawList = Array.isArray(leaf.value) ? leaf.value : [];
        const list = rawList.map((element) => resolvePlaceholder(element, ctx));
        if (list.some((element) => element === UNRESOLVED)) {
            return false;
        }
        const contained = list.some((element) => looseEq(fieldValue, element));
        return op === "in" ? contained : !contained;
    }

    const value = resolvePlaceholder(leaf.value, ctx);
    if (value === UNRESOLVED) {
        return false;
    }

    switch (op) {
        case "eq":
            return looseEq(fieldValue, value);
        case "neq":
            return !looseEq(fieldValue, value);
        case "like":
            return typeof fieldValue === "string" && likeToRegExp(value).test(fieldValue);
        case "notlike":
            return !(typeof fieldValue === "string" && likeToRegExp(value).test(fieldValue));
        case "lt":
            return (fieldValue as never) < (value as never);
        case "lte":
            return (fieldValue as never) <= (value as never);
        case "gt":
            return (fieldValue as never) > (value as never);
        case "gte":
            return (fieldValue as never) >= (value as never);
        default:
            // Unknown / unsupported operator → non-matching (fail closed).
            return false;
    }
};

const isExistsNode = (node: Record<string, unknown>): boolean =>
    "exists" in node ||
    (typeof node.not === "object" && node.not !== null && "exists" in (node.not as object));

/** Recursively evaluate a grammar node (group, leaf, or excluded exists-node). */
const evalNode = (
    record: RecordLike | null | undefined,
    node: unknown,
    ctx: RecordFilterContext
): boolean => {
    if (node == null || typeof node !== "object") {
        return false;
    }
    const obj = node as Record<string, unknown>;

    // `exists` / `not.exists` need a DB round-trip — unsupported for synchronous
    // client-side button visibility.
    if (isExistsNode(obj)) {
        return false;
    }
    if (Array.isArray(obj.and)) {
        return obj.and.every((child) => evalNode(record, child, ctx));
    }
    if (Array.isArray(obj.or)) {
        return obj.or.some((child) => evalNode(record, child, ctx));
    }
    if ("field" in obj) {
        return evalLeaf(getFieldValue(record, obj.field as string), obj as Leaf, ctx);
    }
    return false;
};

/** True when a filter object is a boolean group node `{and|or:[...]}`. */
export const isRecordFilterGroup = (filter: unknown): boolean =>
    filter != null &&
    typeof filter === "object" &&
    (Array.isArray((filter as Record<string, unknown>).and) ||
        Array.isArray((filter as Record<string, unknown>).or));

/**
 * True when a filter object is a bare leaf comparison `{ field, op?, value? }` — a single
 * condition that is NOT wrapped in an `and`/`or` group. This is valid grammar (the backend
 * filter param accepts a top-level comparison directly), so it must be recognised as grammar
 * rather than mistaken for a legacy flat `{ key: value }` map — otherwise its own keys
 * (`field` / `op` / `value`) get iterated as three separate conditions. A comparison carries
 * ONLY the `field` / `op` / `value` keys, which distinguishes it from a legacy flat map that
 * merely happens to contain a `field` column.
 */
export const isRecordFilterLeaf = (filter: unknown): boolean => {
    if (filter == null || typeof filter !== "object" || Array.isArray(filter)) {
        return false;
    }
    const obj = filter as Record<string, unknown>;
    return (
        typeof obj.field === "string" &&
        Object.keys(obj).every((key) => key === "field" || key === "op" || key === "value")
    );
};

/**
 * True when a filter uses the record_filter grammar — either a boolean group `{and|or:[...]}`
 * or a bare leaf comparison `{ field, op, value }` — as opposed to a legacy flat `{ key: value }`
 * map.
 */
export const isRecordFilterGrammar = (filter: unknown): boolean =>
    isRecordFilterGroup(filter) || isRecordFilterLeaf(filter);

/** Evaluate a legacy flat filter: OR-across-keys strict equality on top-level columns. */
const matchesLegacyFlat = (
    record: RecordLike | null | undefined,
    filter: Record<string, unknown>
): boolean =>
    Object.entries(filter).some(([key, value]) => record != null && record[key] === value);

/**
 * Decide whether `record` satisfies `filter`.
 *
 * A missing/non-object filter matches everything (returns `true`). A new-grammar
 * filter is evaluated recursively; any other object is treated as a legacy flat
 * map. See the module docstring for the supported grammar and placeholders.
 */
export const matchesRecordFilter = (
    record: RecordLike | null | undefined,
    filter: unknown,
    context: RecordFilterContext = {}
): boolean => {
    if (filter == null || typeof filter !== "object") {
        return true;
    }
    const ctx: RecordFilterContext = {
        currentUserId: context.currentUserId ?? null,
        workspaceId: context.workspaceId ?? null,
        now: context.now,
    };
    if (isRecordFilterGrammar(filter)) {
        return evalNode(record, filter, ctx);
    }
    return matchesLegacyFlat(record, filter as Record<string, unknown>);
};
